import { useEffect, useState, type MouseEvent } from 'react';
import {
  Avatar,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LaptopIcon from '@mui/icons-material/Laptop';
import InsightsIcon from '@mui/icons-material/Insights';
import ImportantDevicesIcon from '@mui/icons-material/ImportantDevices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import RedeemIcon from '@mui/icons-material/Redeem';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import {
  ambienteAtual,
  carregarMenuSistemas,
  type HostSistema,
  type Sistema,
} from '../../lib/sistemas-sischef';

const ICON_MAP: Record<string, React.ComponentType> = {
  storefront: StorefrontIcon,
  laptop: LaptopIcon,
  insights: InsightsIcon,
  important_devices: ImportantDevicesIcon,
  support_agent: SupportAgentIcon,
  restaurant_menu: RestaurantMenuIcon,
  redeem: RedeemIcon,
};

interface MenuSistemasProps {
  selected?: string;
}

export function MenuSistemas({ selected = 'cardapio' }: MenuSistemasProps): JSX.Element {
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  const [sistemas, setSistemas] = useState<Sistema[]>([]);
  const [hosts, setHosts] = useState<Record<string, HostSistema>>({});
  const token = useSelector((s: RootState) => s.adminAuth.token);
  const aberto = Boolean(ancora);

  useEffect(() => {
    void carregarMenuSistemas().then((config) => {
      setSistemas(config?.sistemas ?? []);
      setHosts(config?.host ?? {});
    });
  }, []);

  function abrir(e: MouseEvent<HTMLElement>): void {
    setAncora(e.currentTarget);
  }

  function fechar(): void {
    setAncora(null);
  }

  function buildUrl(sistema: Sistema): string {
    const environment = ambienteAtual();
    const hostConfig = hosts[sistema.hostKey];

    if (!hostConfig) {
      console.warn(`Host não configurado para: ${sistema.hostKey}`);
      return '#';
    }

    const host = hostConfig[environment];
    if (!host) {
      console.warn(`Host não configurado para ${sistema.hostKey} em ${environment}`);
      return '#';
    }

    // Autologin por caminho: {token} é substituído pelo JWT (quando há token)
    if (token && sistema.tokenPath) {
      return host + sistema.tokenPath.replace('{token}', encodeURIComponent(token));
    }

    let url = host + (sistema.path || '');

    // Adicionar token como parâmetro se necessário
    if (sistema.tokenParam && token) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}${sistema.tokenParam}=${encodeURIComponent(token)}`;
    }

    return url;
  }

  function getIconComponent(iconName: string) {
    const IconComponent = ICON_MAP[iconName] || StorefrontIcon;
    return <IconComponent />;
  }

  return (
    <>
      <List disablePadding aria-label="Sistemas" sx={{ width: '100%' }}>
        <ListItemButton onClick={abrir} aria-expanded={aberto ? 'true' : undefined}>
          <ListItemAvatar>
            <Avatar alt="Sischef" src="/logo-sischef.svg" />
          </ListItemAvatar>
          <ListItemText
            primary="Sischef "
            primaryTypographyProps={{
              style: { fontSize: '20px', fontWeight: '500', letterSpacing: '0px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' },
            }}
            secondary="Cardápio"
            secondaryTypographyProps={{
              style: { marginTop: '-5px' },
            }}
          />
          <MultipleStopIcon />
        </ListItemButton>
      </List>

      <Menu keepMounted anchorEl={ancora} open={aberto} onClose={fechar}>
        <MenuList disablePadding>
          {sistemas.length === 0 ? (
            <MenuItem disabled>
              <ListItemText
                primary="Menu indisponível"
                secondary="Tente novamente em alguns instantes"
                primaryTypographyProps={{ sx: { fontSize: 14 } }}
                secondaryTypographyProps={{ sx: { fontSize: 12 } }}
              />
            </MenuItem>
          ) : (
            sistemas.map((sistema) => {
              const isSelected = sistema.id === selected;
              const url = buildUrl(sistema);
              const disabled = !url || url === '#';

              return (
                <MenuItem
                  key={sistema.id}
                  component="a"
                  href={url}
                  target={sistema.target}
                  rel={sistema.target === '_blank' ? 'noopener noreferrer' : undefined}
                  selected={isSelected}
                  disabled={disabled}
                  onClick={fechar}
                >
                  <ListItemIcon>{getIconComponent(sistema.icone)}</ListItemIcon>
                  <ListItemText primary={sistema.nome} secondary={sistema.descricao} />
                </MenuItem>
              );
            })
          )}
        </MenuList>
      </Menu>
    </>
  );
}
